import { FileText, Download, ExternalLink, BookOpen, Lightbulb, Mail } from 'lucide-react'

interface Guide {
  title: string
  description: string
  filename: string
  icon: typeof FileText
}

const guides: Guide[] = [
  {
    title: 'Trade Prospector Sales Guide',
    description: 'Complete guide to using Trade Prospector for sales. Covers the sales process, using the CRM, managing prospects, and closing deals.',
    filename: 'Trade Prospector Sales Guide.pdf',
    icon: BookOpen,
  },
  {
    title: 'Sales Tips and Tricks',
    description: 'Proven techniques and strategies for successful sales calls. Includes objection handling, rapport building, and closing techniques.',
    filename: 'Sales Tips and Tricks.pdf',
    icon: Lightbulb,
  },
  {
    title: 'Example Prospecting Letter',
    description: 'Template letter for reaching out to prospects. Use this as a guide for crafting your own professional outreach communications.',
    filename: 'Example-Prospecting-Letter.pdf',
    icon: Mail,
  },
]

export default function SalesGuides() {
  const handleView = (filename: string) => {
    window.open(`/guides/${encodeURIComponent(filename)}`, '_blank')
  }

  const handleDownload = (filename: string) => {
    const link = document.createElement('a')
    link.href = `/guides/${encodeURIComponent(filename)}`
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Sales Guides</h1>
        <p className="mt-1 text-slate-400">
          Essential resources and documentation to help you succeed in sales
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {guides.map((guide) => (
          <div
            key={guide.filename}
            className="card p-6 flex flex-col"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#f39c13] to-[#d68910] flex items-center justify-center">
                <guide.icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-white text-lg leading-tight">
                  {guide.title}
                </h3>
              </div>
            </div>

            <p className="mt-4 text-slate-400 text-sm flex-1">
              {guide.description}
            </p>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => handleView(guide.filename)}
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                View
              </button>
              <button
                onClick={() => handleDownload(guide.filename)}
                className="flex-1 btn-secondary flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="card p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <FileText className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="font-medium text-white">Need Additional Resources?</h3>
            <p className="mt-1 text-slate-400 text-sm">
              Contact your manager if you need additional training materials or have questions about the sales process.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
